// What the update warning shows (DR-0098), from what the servers say. Pure, for tests: serverNotice.ts asks the
// servers and draws the bar.
import { SERVER_NOTICE_LINGER_MS, type ServerNotice, type ServerNoticeReply } from "@act/shared";

/** The last minute before a reset: the bar pulses. */
const URGENT_MS = 60_000;
/** Once the home server is back, how long the bar waits for the other servers a reset takes down (the game regions;
 *  on 13 Oct 2026 SEA came back 3 minutes after US), so a region a deploy left out can't hold it up for long. */
export const OTHERS_GRACE_MS = 5 * 60_000;

export type NoticeView =
  | { mode: "hidden" }
  | { mode: "countdown"; text: string; remainingMs: number; urgent: boolean }
  | { mode: "resetting" };

/** Keeps the notice the home server last gave, and the gap between the server's clock and the page's, until it's
 *  taken down or the reset is over: the home server and every other server seen while it was up (the game regions)
 *  have answered as new processes, or the others have had their grace. */
export class NoticeTracker {
  private notice: ServerNotice | null = null;
  private noticeBoot: string | null = null;
  private skewMs = 0;
  /** Notices seen through to their reset, never shown again (an old process answering late). */
  private readonly over = new Set<string>();
  /** The other servers' boot ids as first seen while the notice was up, and those since back as new processes. */
  private readonly boots = new Map<string, string>();
  private readonly back = new Set<string>();
  /** When the home server came back as a new process (the page's clock), or null. */
  private homeBackAt: number | null = null;

  /** A reply from the home server's GET /notice, received when the page's clock read `pageNow`. */
  apply(reply: ServerNoticeReply, pageNow: number): void {
    this.skewMs = reply.now - pageNow;
    const fresh = reply.notice && !this.over.has(reply.notice.id) ? reply.notice : null;
    if (fresh && fresh.id !== this.notice?.id) {
      this.finish();
      this.notice = fresh;
      this.noticeBoot = reply.bootId;
      return;
    }
    if (!this.notice) return;
    if (reply.bootId !== this.noticeBoot) this.homeBackAt ??= pageNow; // a new process: its part of the reset is done
    else if (!reply.notice) this.finish(); // taken down
  }

  /** Another server a reset takes down (a game region) answered with this boot id. */
  applyOther(server: string, bootId: string): void {
    if (!this.notice) return;
    const before = this.boots.get(server);
    if (before === undefined) this.boots.set(server, bootId);
    else if (bootId !== before) this.back.add(server);
  }

  /** Whether the other servers are worth asking: only while a notice is up. */
  watching(): boolean {
    return this.notice !== null;
  }

  view(pageNow: number): NoticeView {
    const notice = this.notice;
    if (!notice) return { mode: "hidden" };
    if (this.homeBackAt !== null && ([...this.boots.keys()].every((server) => this.back.has(server)) || pageNow - this.homeBackAt > OTHERS_GRACE_MS)) {
      this.finish();
      return { mode: "hidden" };
    }
    const remainingMs = notice.resetAt - (pageNow + this.skewMs);
    if (remainingMs > 0) return { mode: "countdown", text: notice.text, remainingMs, urgent: remainingMs <= URGENT_MS };
    return -remainingMs > SERVER_NOTICE_LINGER_MS ? { mode: "hidden" } : { mode: "resetting" };
  }

  private finish(): void {
    if (this.notice) this.over.add(this.notice.id);
    this.notice = null;
    this.noticeBoot = null;
    this.boots.clear();
    this.back.clear();
    this.homeBackAt = null;
  }
}

/** 4:59, 0:07 (rounded up, so it reads 0:01 until the reset). */
export function formatCountdown(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}
